<template>
  <div class="mail-event-list">

    <v-row>
      <v-col cols="12">
        <search-field
            :search.sync="filters.search"
            :label="$t('search')"
            :placeholder="$t('search-by-email-or-subject')"
            solo
            dense
            clearable
            hide-details
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card>
          <v-card-text>
            <v-row class="align-center" dense>
              <v-col>
                <date-picker
                    v-model="filters.dateRange"
                    :label="$t('period')"
                    solo
                    clearable
                    hide-details
                    range
                />
              </v-col>
              <v-col v-if="showEventFilter">
                <v-select
                    v-model="filters.eventTypes"
                    :items="eventTypes"
                    :label="$t('mail-event.event-type')"
                    solo
                    dense
                    clearable
                    multiple
                    deletable-chips
                    chips
                    small-chips
                    hide-details
                />
              </v-col>
              <v-col v-if="!organizationId">
                <organization-select
                    v-model="filters.organizationId"
                    :label="$t('organization.organization')"
                    dense
                    solo
                    selection-with-no-extra
                    clearable
                />
              </v-col>
              <v-col v-if="!customerId">
                <customer-select
                    v-model="filters.customerId"
                    :label="$tc('customer.customer')"
                    solo
                    clearable
                    selection-with-no-extra
                />
              </v-col>
              <v-col v-if="!userId">
                <user-select
                    v-model="filters.userId"
                    :label="$tc('user.user')"
                    solo
                    clearable
                    selection-with-no-extra
                />
              </v-col>
              <v-col cols="auto">
                <v-btn small icon @click="refresh" :loading="loading">
                  <v-icon >mdi-refresh</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-data-table
            class="mail-event-table"
            :headers="headers"
            :items="items"
            :loading="loading"
            :server-items-length="total"
            :footer-props="{
              itemsPerPageOptions: [15, 50, 100]
            }"
            :options.sync="options"
        >
          <template #item.email="{item}">
            <div class="email-cell d-flex align-center">
              <span class="flex-grow-1">{{ item.email }}</span>
              <v-btn
                  icon
                  x-small
                  @click="search(item.email)"
                  :title="$t('search-by-email')"
                  class="ml-2"
              >
                <v-icon x-small>mdi-magnify</v-icon>
              </v-btn>
            </div>
          </template>

          <template #item.subject="{item}">
            <div class="subject-cell d-flex align-center">
              <span class="flex-grow-1 text-truncate">{{ item.subject || '-' }}</span>
              <v-btn
                  v-if="item.subject"
                  icon
                  x-small
                  @click="search(item.subject)"
                  :title="$t('search-by-subject')"
                  class="ml-2"
              >
                <v-icon x-small>mdi-magnify</v-icon>
              </v-btn>
            </div>
          </template>

          <template #item.events="{item}">
            <div class="events-cell">
              <v-chip
                  v-for="(groupedEvent, eventType) in groupEvents(item.events)"
                  :key="eventType"
                  :color="getEventColor(eventType)"
                  :text-color="getTextColor(getEventColor(eventType))"
                  small
                  class="mr-1 mb-1"
                  :class="{'bounce-event': eventType === 'bounce'}"
              >
                <v-badge
                    v-if="groupedEvent.count > 1"
                    :content="groupedEvent.count"
                    inline
                    color="rgba(0,0,0,0.3)"
                >
                  {{ getEventLabel(eventType) }}
                </v-badge>
                <template v-else>
                  {{ getEventLabel(eventType) }}
                </template>
              </v-chip>
            </div>
          </template>

          <template #item.createdAt="{value}">
            <div class="text-no-wrap">
              {{ value|formatDate }}
            </div>
          </template>

          <template #item.options="{item}">
            <div class="options">
              <v-btn icon rounded @click="openDetailModal(item)" class="mr-2">
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <mail-event-detail-modal
      ref="mailEventDetailModal"
      @search="search"
    />
  </div>
</template>

<script>
import MailEventRepository from "@repository/MailEventRepository";
import CustomerSelect from "@blocks/Select/CustomerSelect.vue";
import OrganizationSelect from "@blocks/Select/OrganizationSelect.vue";
import UserSelect from "@blocks/Select/UserSelect.vue";
import DatePicker from "@blocks/DatePicker.vue";
import MailEventDetailModal from "@modals/MailEventDetailModal.vue";

export default {
  components: {
    CustomerSelect,
    OrganizationSelect,
    UserSelect,
    DatePicker,
    MailEventDetailModal,
  },

  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 20,
        sortBy: ['createdAt'],
        sortDesc: [true],
      },
      filters: {
        search: null,
        customerId: this.customerId,
        userId: this.userId,
        organizationId: this.organizationId,
        dateRange: [],
        eventTypes: [],
      },
    }
  },

  props: {
    customerId: {type: String, default: null},
    userId: {type: String, default: null},
    organizationId: {type: String, default: null},
    showEventFilter: {type: Boolean, default: true},
  },

  mounted() {
    // Initialize filters from props
    if (this.customerId) {
      this.filters.customerId = this.customerId
    }
    if (this.userId) {
      this.filters.userId = this.userId
    }
    if (this.organizationId) {
      this.filters.organizationId = this.organizationId
    }
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.loadData()
      }
    },

    options() {
      this.loadData()
    },

    customerId(val) {
      this.filters.customerId = val
    },

    organizationId(val) {
      this.filters.organizationId = val
    },

    userId(val) {
      this.filters.userId = val
    },
  },

  computed: {
    headers() {
      return [
        {text: this.$t('email'), value: "email", align: 'left'},
        {text: this.$t('mail-event.subject'), value: "subject", align: 'left'},
        {text: this.$t('mail-event.events'), value: "events", align: 'left', sortable: false},
        {text: this.$t('date'), value: "createdAt", align: 'left'},
        {text: "", value: "options", sortable: false},
      ]
    },

    eventTypes() {
      return [
        {text: this.$t('mail-event.sent'), value: 'sent'},
        {text: this.$t('mail-event.open'), value: 'open'},
        {text: this.$t('mail-event.click'), value: 'click'},
        {text: this.$t('mail-event.bounce'), value: 'bounce'},
        {text: this.$t('mail-event.blocked'), value: 'blocked'},
        {text: this.$t('mail-event.spam'), value: 'spam'},
        {text: this.$t('mail-event.unsub'), value: 'unsub'},
      ]
    },
  },

  methods: {
    refresh() {
      // Reset to page 1, which will trigger loadData via the watcher
      if (this.options.page === 1) {
        // Already on page 1, just reload
        this.loadData()
      } else {
        // Go to page 1, watcher will trigger loadData
        this.options.page = 1
      }
    },

    loadData() {
      if (!this.options) return

      return new Promise((resolve) => {
        let offset = this.options.itemsPerPage * this.options.page - this.options.itemsPerPage;
        let limit = this.options.itemsPerPage;
        let orderBy = this.options.sortBy.length > 0 ? this.options.sortBy[0] : 'createdAt'
        let orderDirection = this.options.sortDesc.length > 0 && this.options.sortDesc[0] ? 'desc' : 'asc'

        this.loading = true

        const data = {
          offset,
          limit,
          orderBy,
          orderDirection,
        }

        if (this.filters.search) {
          data.search = this.filters.search
        }

        if (this.filters.customerId) {
          data.customerId = this.filters.customerId
        }

        if (this.filters.organizationId) {
          data.organizationId = this.filters.organizationId
        }

        if (this.filters.userId) {
          data.userId = this.filters.userId
        }

        if (this.filters.dateRange && this.filters.dateRange.length > 0) {
          const dates = this.filters.dateRange.toSorted()
          data.startDate = dates[0]
          if (dates.length > 1) {
            data.endDate = dates[1]
          } else {
            data.endDate = dates[0]
          }
        }

        if (this.filters.eventTypes && this.filters.eventTypes.length > 0) {
          data.eventTypes = this.filters.eventTypes
        }

        MailEventRepository.search(data).then(result => {
          this.items = result.items
          this.total = result.total
          this.loading = false
          resolve()
        }).catch(err => {
          this.notifyError(err)
          this.loading = false
          resolve()
        })
      })
    },


    getEventColor(event) {
      const colors = {
        sent: 'blue',
        open: 'teal',
        click: 'purple',
        bounce: 'red',
        blocked: 'orange',
        spam: 'deep-orange',
        unsub: 'grey',
      }
      return colors[event] || 'grey'
    },

    getTextColor(colorName) {
      // Couleurs claires qui nécessitent du texte noir
      const lightColors = ['yellow', 'lime', 'cyan', 'amber', 'light-blue', 'light-green']

      // Couleurs sombres qui nécessitent du texte blanc
      const darkColors = ['blue', 'green', 'teal', 'purple', 'red', 'orange', 'grey', 'indigo', 'deep-purple', 'deep-orange', 'brown', 'blue-grey']

      if (lightColors.includes(colorName)) {
        return 'black'
      }
      if (darkColors.includes(colorName)) {
        return 'white'
      }

      // Par défaut, texte blanc
      return 'white'
    },

    getEventLabel(event) {
      const labels = {
        sent: this.$t('mail-event.sent'),
        open: this.$t('mail-event.open'),
        click: this.$t('mail-event.click'),
        bounce: this.$t('mail-event.bounce'),
        blocked: this.$t('mail-event.blocked'),
        spam: this.$t('mail-event.spam'),
        unsub: this.$t('mail-event.unsub'),
      }
      return labels[event] || event
    },

    groupEvents(events) {
      const grouped = {}
      events.forEach(event => {
        if (!grouped[event.event]) {
          grouped[event.event] = {
            count: 0,
            events: []
          }
        }
        grouped[event.event].count++
        grouped[event.event].events.push(event)
      })
      return grouped
    },

    search(value) {
      this.filters.search = value
    },

    openDetailModal(item) {
      this.$refs.mailEventDetailModal.open(item)
    },
  }
}
</script>

<style lang="scss">
.mail-event-list {
  .v-input__control , .v-input__slot{
    min-height: 48px !important;
  }
  .table {
    .mail-event-table {
      tr {
        td {
          .email-cell {
            font-weight: 500;
          }

          .subject-cell {
            max-width: 300px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .events-cell {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;

            .bounce-event {
              font-weight: bold;
              animation: pulse 2s infinite;
            }
          }
        }
      }

      .options {
        .v-btn--icon {
          border: 1px solid #758093;
          width: 30px;
          height: 30px;
          i {
            font-size: 16px;
            color: #758093;
          }
        }
      }
    }
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
